import {create} from 'zustand'
import moment from 'moment'
import {getStoredDates, storeDates} from '../utils/storedDates.js'
import {Order} from '../../server/functions/orders/orders.types.js'
import {AddressesModel, StopsModel, RoutesModel, ParcelsModel, AddressesModelReversed, ParcelsModelReversed} from '../../server/functions/analytics/analytics.types.js'
import {AnalyticsViewType, groupDepths, groupReversedDepths, groupRoutesDepth, groupStopsDepth, parcelsGroupDepth, parcelsGroupReversedDepth} from 'src/views/planner/Analytics.js'

export type Query = {
    key: string
    title: string
    operator: '=' | '==' | '!=' | '>' | '<'
    text: string
}

interface AnalyticsState {
    analyticsStartDate: string
    analyticsEndDate: string
    lastFetchedStartDate: string
    lastFetchedEndDate: string
    analyticsQueries: {
        [selectedViewKey: string]: Query[]
    }
    analyticsOrders: Order[]
    analyticsModel: AddressesModel
    analyticsModelReversed: AddressesModelReversed
    parcelsModel: ParcelsModel
    parcelsModelReversed: ParcelsModelReversed
    stopsModel: StopsModel
    routesModel: RoutesModel
    analyticsModelOriginal: AddressesModel
    analyticsModelReversedOriginal: AddressesModelReversed
    parcelsModelOriginal: ParcelsModel
    parcelsModelReversedOriginal: ParcelsModelReversed
    stopsModelOriginal: StopsModel
    routesModelOriginal: RoutesModel
    loading: boolean
    selectedAnalytics: AnalyticsViewType['key']
    selectedGroup: {
        [selectedViewKey: string]: string
    }
    subGroup: {
        [selectedViewKey: string]: string
    }
    selectedSpecific: string
    showFilterCard: boolean
}

const storedDates = getStoredDates('analytics')

const initialState: AnalyticsState = {
    analyticsStartDate: storedDates?.startDate || (process.env.NODE_ENV === 'local' ? moment('01-01-2025', 'DD-MM-YYYY').format('YYYY-MM-DD') : moment().startOf('day').format('YYYY-MM-DD')),
    analyticsEndDate: storedDates?.endDate || (process.env.NODE_ENV === 'local' ? moment('31-01-2025', 'DD-MM-YYYY').format('YYYY-MM-DD') : moment().endOf('day').format('YYYY-MM-DD')),
    lastFetchedStartDate: '',
    lastFetchedEndDate: '',
    analyticsQueries: {
        amountOfOrders: [],
        amountOfAddresses: [],
        amountOfColli: [],
        amountOfParcels: [],
        plannedStops: [],
        amountOfDeliveryAttempts: [],
        differenceRouteDuration: [],
        colloStatus: []
    },
    analyticsOrders: [],
    analyticsModel: {},
    analyticsModelReversed: {},
    parcelsModel: {},
    parcelsModelReversed: {},
    stopsModel: {},
    routesModel: {},
    analyticsModelOriginal: {},
    analyticsModelReversedOriginal: {},
    parcelsModelOriginal: {},
    parcelsModelReversedOriginal: {},
    stopsModelOriginal: {},
    routesModelOriginal: {},
    loading: true,
    selectedAnalytics: 'amountOfAddresses',
    selectedGroup: {
        amountOfOrders: 'customer',
        amountOfAddresses: 'customer',
        amountOfColli: 'customer',
        amountOfParcels: 'customer',
        plannedStops: 'customer',
        amountOfDeliveryAttempts: 'customer',
        differenceRouteDuration: 'customer',
        colloStatus: 'customer'
    },
    subGroup: {
        amountOfOrders: '',
        amountOfAddresses: '',
        amountOfColli: '',
        amountOfParcels: '',
        plannedStops: '',
        amountOfDeliveryAttempts: '',
        differenceRouteDuration: '',
        colloStatus: ''
    },
    selectedSpecific: '',
    showFilterCard: process.env.NODE_ENV === 'local'
}

const useAnalyticsStore = create<AnalyticsState>(() => (initialState))

const setAnalyticsDates = (startDate: string, endDate: string) => {
    useAnalyticsStore.setState({analyticsStartDate: startDate, analyticsEndDate: endDate})
    storeDates('analytics', startDate, endDate)
}

const setLastFetchedStartDate = (startDate: string) => {
    useAnalyticsStore.setState({lastFetchedStartDate: startDate})
}

const setLastFetchedEndDate = (endDate: string) => {
    useAnalyticsStore.setState({lastFetchedEndDate: endDate})
}

const setAnalyticsQueries = (queries: Query[], view: string) => {
    useAnalyticsStore.setState((state) => ({
        analyticsQueries: {
            ...state.analyticsQueries,
            [view]: queries
        }
    }))

    if (queries.length === 0) {
        const {
            analyticsModelOriginal,
            analyticsModelReversedOriginal,
            parcelsModelOriginal,
            parcelsModelReversedOriginal,
            stopsModelOriginal,
            routesModelOriginal
        } = useAnalyticsStore.getState()

        useAnalyticsStore.setState({
            analyticsModel: analyticsModelOriginal,
            analyticsModelReversed: analyticsModelReversedOriginal,
            parcelsModel: parcelsModelOriginal,
            parcelsModelReversed: parcelsModelReversedOriginal,
            stopsModel: stopsModelOriginal,
            routesModel: routesModelOriginal
        })
    }

    useAnalyticsStore.setState({loading: true})
    filterQueries(view)
    useAnalyticsStore.setState({loading: false})
}

const setAnalyticsOrders = (orders: Order[]) => {
    useAnalyticsStore.setState({analyticsOrders: orders})
}

const getAnalyticsModels = async (socketEvent: string) => {
    const {analyticsStartDate, analyticsEndDate} = useAnalyticsStore.getState()

    const res = await fetch(`/analytics/model?startDate=${analyticsStartDate}&endDate=${analyticsEndDate}&socket=${socketEvent}`, {
        method: 'GET',
        headers: {
            'Authorization': sessionStorage.token,
            'Content-Type': 'application/json'
        }
    })

    if (res.ok) {
        const {addressesModel, addressesModelReversed, parcelsModel, parcelsModelReversed, stopsModel, routesModel} = await res.json()
        useAnalyticsStore.setState({
            analyticsModel: addressesModel,
            analyticsModelReversed: addressesModelReversed,
            parcelsModel: parcelsModel,
            parcelsModelReversed: parcelsModelReversed,
            stopsModel: stopsModel,
            routesModel: routesModel,
            analyticsModelOriginal: addressesModel,
            analyticsModelReversedOriginal: addressesModelReversed,
            parcelsModelOriginal: parcelsModel,
            parcelsModelReversedOriginal: parcelsModelReversed,
            stopsModelOriginal: stopsModel,
            routesModelOriginal: routesModel
        })
    } else {
        alert(`${res.status} ${res.statusText}`)
    }
}

const setSelectedAnalytics = (selectedAnalytics: AnalyticsViewType['key']) => {
    useAnalyticsStore.setState({selectedAnalytics})
}

const setSelectedGroup = (selectedViewKey: string, group: string) => {
    const {selectedGroup} = useAnalyticsStore.getState()
    useAnalyticsStore.setState({selectedGroup: {...selectedGroup, [selectedViewKey]: group}})
}

const setSubGroup = (selectedViewKey: string, subGroup: string) => {
    const {subGroup: currentSubGroup} = useAnalyticsStore.getState()
    useAnalyticsStore.setState({subGroup: {...currentSubGroup, [selectedViewKey]: subGroup}})
}

function buildPredicate(queries: Query[]) {
    return (key: string): boolean => {
        const left = key.trim().toLowerCase()

        // Separate != queries and others
        const notEqualsQueries = queries.filter((q) => q.operator === '!=')
        const otherQueries = queries.filter((q) => q.operator !== '!=')

        // If any notEquals query matches, exclude
        for (const query of notEqualsQueries) {
            const right = query.text.trim().toLowerCase()
            if (left.indexOf(right) > -1) {
                return false
            }
        }

        if (otherQueries.length > 0) {
            return otherQueries.some((query) => {
                const right = query.text.trim().toLowerCase()
                const leftNum = Number(left)
                const rightNum = Number(right)
                const bothNumeric = !isNaN(leftNum) && !isNaN(rightNum)

                switch (query.operator) {
                    case '=':
                        return left.indexOf(right) > -1
                    case '==':
                        return left === right
                    case '>':
                        return bothNumeric ? leftNum > rightNum : left > right
                    case '<':
                        return bothNumeric ? leftNum < rightNum : left < right
                    default:
                        return false
                }
            })
        }

        return true
    }
}

function filterQueries(view: string) {
    const state = useAnalyticsStore.getState()
    const queries = state.analyticsQueries[view] ?? []

    const connectedModels = {
        amountOfAddresses: ['analyticsModel', 'analyticsModelReversed'],
        amountOfParcels: ['parcelsModel', 'parcelsModelReversed'],
        amountOfDeliveryAttempts: ['stopsModel'],
        differenceRouteDuration: ['routesModel']
    }[view]

    if (!connectedModels) return

    // Reset models if no queries
    if (queries.length === 0) {
        const resetState: Record<string, any> = {}

        for (const model of connectedModels) {
            resetState[model] = state[`${model}Original`]
        }

        useAnalyticsStore.setState(resetState)
        return
    }

    const grouped = groupQueriesByModelAndDepth(queries)

    const entries = [...grouped.entries()].sort(
        ([a], [b]) => {
            const depthA = Number(a.split(':')[1])
            const depthB = Number(b.split(':')[1])
            return depthA - depthB
        }
    )

    const isFiltered: Record<string, boolean> = {}

    for (const model of connectedModels) {
        isFiltered[model] = false
    }

    for (const [groupKey, groupQueries] of entries) {
        const [model, depthStr] = groupKey.split(':')
        const depth = Number(depthStr)

        if (!connectedModels.includes(model)) continue

        const stateModelName = isFiltered[model] ?
            model :
            `${model}Original`

        const stateModel = useAnalyticsStore.getState()[stateModelName]

        const predicate = buildPredicate(groupQueries)

        const filteredModel = filterAtDepth(
            stateModel,
            depth,
            predicate,
            groupQueries[0].key,
            0
        )

        useAnalyticsStore.setState({
            [model]: filteredModel
        })

        isFiltered[model] = true
    }

    // After filtering, check if selectedSpecific is still present
    const {selectedSpecific} = useAnalyticsStore.getState()
    if (selectedSpecific) {
        // Find the filtered model for the current view
        const modelKey = {
            amountOfAddresses: 'analyticsModel',
            amountOfParcels: 'parcelsModel',
            amountOfDeliveryAttempts: 'stopsModel',
            differenceRouteDuration: 'routesModel'
        }[view]

        const filteredModel = useAnalyticsStore.getState()[modelKey]

        // Check if selectedSpecific exists at any depth in the filtered model
        let found = false
        if (filteredModel && typeof filteredModel === 'object') {
            function search(obj: any): boolean {
                if (typeof obj !== 'object' || obj === null) return false
                if (selectedSpecific in obj) return true
                return Object.values(obj).some(search)
            }
            found = search(filteredModel)
        }

        if (!found) {
            setSelectedSpecific('')
        }
    }
}

function groupQueriesByModelAndDepth(queries: Query[]) {
    const map = new Map<string, Query[]>()

    for (const query of queries) {
        for (const [model, depths] of Object.entries({
            analyticsModel: groupDepths,
            analyticsModelReversed: groupReversedDepths,
            parcelsModel: parcelsGroupDepth,
            parcelsModelReversed: parcelsGroupReversedDepth,
            stopsModel: groupStopsDepth,
            routesModel: groupRoutesDepth
        })) {
            const depth = depths[query.key]
            if (isNaN(depth)) continue

            const key = `${model}:${depth}`
            if (!map.has(key)) map.set(key, [])
            map.get(key)!.push(query)
        }
    }

    return map
}

function filterAtDepth(
    obj: Record<string, any>,
    targetDepth: number,
    shouldKeep: (key: string, value: any) => boolean,
    queryKey: string,
    currentDepth = 0
): Record<string, any> {
    if (typeof obj !== 'object' || obj === null) return obj

    let entries = []

    for (const [key, value] of Object.entries(obj)) {
        if (currentDepth === targetDepth) {
            if (queryKey === 'option' || queryKey === 'deliveryZone') {
                const keys = key.split(',').map((k) => k.trim()).filter((k) => k)
                for (const k of keys) {
                    if (shouldKeep(k, value)) {
                        entries.push([k, value])
                    }
                }
            } else {
                if (shouldKeep(key, value)) {
                    entries.push([key, value])
                }
            }
        } else {
            const next = filterAtDepth(
                value,
                targetDepth,
                shouldKeep,
                queryKey,
                currentDepth + 1
            )

            if (
                typeof next === 'object' &&
                next !== null &&
                Object.keys(next).length === 0
            ) {
                continue
            }

            entries.push([key, next])
        }
    }

    entries = entries.filter(Boolean) as [string, any][]

    return Object.fromEntries(entries)
}

const setShowFilterCard = (show: boolean) => {
    useAnalyticsStore.setState({showFilterCard: show})
}

const setSelectedSpecific = (value: string) => {
    useAnalyticsStore.setState({selectedSpecific: value})
}

export {
    useAnalyticsStore,
    setAnalyticsDates,
    setLastFetchedStartDate,
    setLastFetchedEndDate,
    setAnalyticsQueries,
    setAnalyticsOrders,
    getAnalyticsModels,
    setSelectedAnalytics,
    setSelectedGroup,
    setSubGroup,
    setShowFilterCard,
    setSelectedSpecific,
    filterQueries
}

export default useAnalyticsStore
